import { createSlice, createEntityAdapter } from "@reduxjs/toolkit";
import { notifyError, notifySuccess } from "./notification.slice";
import { authFailCheck } from "./auth.slice";
import TransactionService from "./../services/TransactionService";
import DividendService from "./../services/DividendService";
import { TransactionType } from "./../constants/SiteConstants";

const getErrorMessage = (data, fallback) => {
  if (!data) return fallback;
  if (typeof data === "string") return data;
  if (data.message) return data.message;
  if (Array.isArray(data) && data[0]?.errorMessage) return data[0].errorMessage;
  if (data.errors?.[0]) {
    const first = data.errors[0];
    return typeof first === "string" ? first : first.errorMessage || fallback;
  }
  return fallback;
};

const transactionAdapter = createEntityAdapter({
  selectId: (transaction) => transaction.id,
});

const initialState = transactionAdapter.getInitialState({
  isLoading: false,
  summary: {
    totalIncome: 0,
    totalExpense: 0,
    total: 0,
    naqd: 0,
    karta: 0,
    bank: 0,
  },
  filter: {
    dateFilterType: "All",
    selectedDate: null,
    fromDate: null,
    toDate: null,
    transactionType: "0",
    entryType: "0",
    keyword: "",
    sortBy: null,
    sortOrder: 1,
    pageNumber: 1,
    pageSize: 25,
  },
});

const transactionSlice = createSlice({
  name: "transactions",
  initialState: initialState,
  reducers: {
    fetchRequest(state, action) {
      state.isLoading = true;
      state.filter = { ...state.filter, ...action.payload };
    },
    fetchFail(state, action) {
      state.isLoading = false;
    },
    fetchTransactionsSuccess(state, action) {
      const { payload } = action;
      state.isLoading = false;
      state.pageCount = payload.pageCount;
      state.recordCount = payload.recordCount;

      state.ids = [];
      state.entities = [];

      payload.items.forEach((item, index) => {
        state.ids[index] = item.id;
        state.entities[item.id] = item;
      });
    },
    fetchTransactionSummarySuccess(state, action) {
      state.summary = action.payload;
    },
    createTransactionSuccess(state, action) {
      state.isLoading = false;
      transactionAdapter.addOne(state, action.payload);
    },
  },
});

export default transactionSlice.reducer;

export const {
  fetchRequest,
  fetchFail,
  fetchTransactionsSuccess,
  createTransactionSuccess,
  fetchTransactionSummarySuccess
} = transactionSlice.actions;

export const transactionSelectors = transactionAdapter.getSelectors(
  (state) => state.transactions
);

export const fetchTransactions = (filter) => async (dispatch) => {
  dispatch(fetchRequest(filter));
  dispatch(fetchTransactionSummary(filter));
  const response = await TransactionService.get(filter);
  if (response.ok) {
    dispatch(fetchTransactionsSuccess(response.data));
  } else {
    dispatch(authFailCheck(response));
    dispatch(fetchFail(response.data));
  }
};
export const fetchTransactionSummary = (filter) => async (dispatch) => {
  dispatch(fetchRequest(filter));

  const response = await TransactionService.getSummary(filter);
  if (response.ok) {
    dispatch(fetchTransactionSummarySuccess(response.data));
  } else {
    dispatch(authFailCheck(response));
    dispatch(fetchFail(response.data));
  }
};

export const createTransaction = (transaction) => async (dispatch) => {
  dispatch(fetchRequest(transactionAdapter.getInitialState().filter));

  const response = await TransactionService.create(transaction);
  const text =
    transaction.transactionType === TransactionType.Income ? "kirim" : "chiqim";
  if (response.ok) {
    const { id } = response.data;
    dispatch(createTransactionSuccess({ id, ...transaction }));
    dispatch(fetchTransactions(initialState.filter));
    dispatch(notifySuccess(`Yangi ${text} kiritildi.`));
  } else {
    dispatch(authFailCheck(response));
    dispatch(
      notifyError(
        getErrorMessage(response.data, `Yangi ${text} yaratishda xatolik yuz berdi.`)
      )
    );
    dispatch(fetchFail(response.data));
  }
};

export const createPaymentTypeTransfer = (data) => async (dispatch, getState) => {
  const filter = getState().transactions.filter;
  dispatch(fetchRequest(filter));
  const response = await TransactionService.transfer(data);
  if (response.ok) {
    dispatch(notifySuccess("Pul o'tkazildi."));
    dispatch(fetchTransactions(filter));
    return { ok: true };
  }
  dispatch(authFailCheck(response));
  dispatch(fetchFail(response.data));
  return {
    ok: false,
    message: getErrorMessage(response.data, "Pul o'tkazishda xatolik yuz berdi."),
  };
};

export const createDividend = (data) => async (dispatch, getState) => {
  const filter = getState().transactions.filter;
  dispatch(fetchRequest(filter));
  const response = await DividendService.create(data);
  if (response.ok) {
    dispatch(notifySuccess("Dividend kiritildi."));
    dispatch(fetchTransactions(filter));
    return { ok: true };
  }
  dispatch(authFailCheck(response));
  dispatch(fetchFail(response.data));
  return {
    ok: false,
    message: getErrorMessage(
      response.data,
      "Dividend yaratishda xatolik yuz berdi."
    ),
  };
};
